/**
 * Anonymous Traffic View
 *
 * The anonymous counterpart to Cell Access / Scraping Detection: those two
 * surfaces are per-USER and therefore blind to traffic that never logged in.
 * This view aggregates the same api_metrics rows by hashed IP instead, which is
 * where scraping actually shows up first.
 *
 * Ported from the retired Grafana "API Metrics" dashboard — panels 2, 8, 9, 10,
 * 13 (country table; the geomap is a follow-up), 14, 15, 16, 17, 18.
 *
 * IMPORTANT — mixed time windows. Panels 10, 14 and 18 are served on a
 * HARDCODED 24h window server-side and ignore period_days. Every one of them is
 * labelled "(24h)" on screen: without that label the numbers read as
 * contradicting the period selector in the tab header.
 */
import * as echarts from 'echarts'
import { useEffect, useRef, useState } from 'preact/hooks'
import {
  getAnonymousTrafficGeo,
  getAnonymousTrafficOverview,
  getAnonymousTrafficSuspicion,
  getAnonymousTrafficTimeseries,
  getAnonymousTrafficTopCells,
} from '../../../services/adminService.js'
import { COLORS, chartTheme } from './shared/chartTheme.js'
import { formatNumber } from './shared/formatters.js'
import { Icons } from './shared/Icons.jsx'
import { KPICard } from './shared/KPICard.jsx'

/** Fixed-window marker. Panels 10/14/18 ignore the period selector. */
const WINDOW_24H = '(24h)'

/** Render a hashed IP in a monospace cell — it is a truncated hash, not an address. */
function IpCell({ ip }) {
  return (
    <td className="endpoint-cell anon-ip-cell" title={ip}>
      {ip || '-'}
    </td>
  )
}

/** Percentages arrive as numbers 0-100; render one decimal, tolerate null. */
function formatPct(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-'
  return `${Number(value).toFixed(1)}%`
}

/** Local timestamp for `last_access` / bucket labels. */
function formatTimestamp(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Section wrapper so every panel carries its own window caption. */
function Panel({ title, caption, children }) {
  return (
    <div className="analytics-table-section">
      <div className="anon-panel-header">
        <h3>{title}</h3>
        {caption && <span className="anon-panel-caption">{caption}</span>}
      </div>
      {children}
    </div>
  )
}

/** Uniform empty state — an empty table body reads as a broken fetch. */
function EmptyRow({ colSpan, label = 'No data in this period' }) {
  return (
    <tr>
      <td colSpan={colSpan} style={{ textAlign: 'center', padding: '24px', color: COLORS.gray }}>
        {label}
      </td>
    </tr>
  )
}

/**
 * Failure state — deliberately NOT the empty state.
 *
 * This is an abuse-detection surface. When a panel's endpoint is down, "No
 * data in this period" is a silent false all-clear: it reads as "nobody is
 * scraping you" when the truth is "we do not know". The two must never look
 * alike, so this row is amber, says "could not load", and carries the reason.
 */
function FailedRow({ colSpan, error }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        style={{ textAlign: 'center', padding: '24px', color: COLORS.orange }}
      >
        <strong>Could not load this panel.</strong>{' '}
        <span style={{ color: COLORS.gray }}>
          This is not the same as "no activity" — the data is unknown.
        </span>
        {error && (
          <div style={{ marginTop: '6px', fontSize: '0.85em', color: COLORS.gray }}>
            {error}
          </div>
        )}
      </td>
    </tr>
  )
}

/**
 * Panel 15 — hourly bot vs human requests.
 *
 * Deliberately NOT the shared TimeSeriesChart: that component labels the x-axis
 * `d.m` from a date, which collapses every hour of a day onto the same tick.
 * This series is hourly, so it needs its own axis formatting.
 */
function BotVsHumanChart({ buckets, height = 280 }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !buckets || buckets.length === 0) return

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' })
    }

    const xData = buckets.map((b) => {
      const d = new Date(b.time)
      if (Number.isNaN(d.getTime())) return String(b.time)
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:00`
    })

    const option = {
      ...chartTheme,
      grid: { left: 55, right: 20, top: 30, bottom: 45 },
      tooltip: { ...chartTheme.tooltip, trigger: 'axis', axisPointer: { type: 'cross' } },
      legend: { ...chartTheme.legend, right: 0, top: 0 },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: COLORS.grid } },
        axisLabel: { color: COLORS.gray, fontSize: 10, rotate: 45 },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: COLORS.gray, fontSize: 11 },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: [
        {
          name: 'Bots',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: buckets.map((b) => b.bots || 0),
          lineStyle: { width: 2, color: COLORS.red },
          itemStyle: { color: COLORS.red },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: COLORS.red + '40' },
              { offset: 1, color: COLORS.red + '05' },
            ]),
          },
        },
        {
          name: 'Humans',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: buckets.map((b) => b.humans || 0),
          lineStyle: { width: 2, color: COLORS.cyan },
          itemStyle: { color: COLORS.cyan },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: COLORS.cyan + '40' },
              { offset: 1, color: COLORS.cyan + '05' },
            ]),
          },
        },
      ],
    }

    chartInstance.current.setOption(option)
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [buckets])

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null }, [])

  if (!buckets || buckets.length === 0) {
    return (
      <div className="analytics-chart-container analytics-chart-empty" style={{ height }}>
        <div className="analytics-chart-header">
          <span className="analytics-chart-title">Bot vs Human Requests (hourly)</span>
        </div>
        <p>No data available</p>
      </div>
    )
  }

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <span className="analytics-chart-title">Bot vs Human Requests (hourly)</span>
        <span style={{ fontSize: '11px', color: COLORS.gray }}>
          Anonymous requests classified by user agent
        </span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  )
}

/** Suspicion badge for panel 18 — same bands as the per-user scraping view. */
function ScoreBadge({ score }) {
  const value = Number(score) || 0
  let color = '#22c55e'
  if (value >= 71) color = COLORS.red
  else if (value >= 46) color = COLORS.orange
  else if (value >= 26) color = '#f59e0b'
  return (
    <span className="anon-score-badge" style={{ color, borderColor: color }}>
      {value}
    </span>
  )
}

/**
 * Main Anonymous Traffic View
 */
export function AnalyticsAnonymousTrafficView({ periodDays }) {
  const [overview, setOverview] = useState(null)
  const [topCells, setTopCells] = useState(null)
  const [timeseries, setTimeseries] = useState(null)
  const [suspicion, setSuspicion] = useState(null)
  const [geo, setGeo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // Per-panel failure reasons for the four best-effort fetches. Null = fine.
  const [panelErrors, setPanelErrors] = useState({})

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setPanelErrors({})

    // The overview bundle is the one hard dependency — the four others are
    // best-effort so a single slow/failing query cannot blank the whole tab.
    // But "best effort" must not mean "silently indistinguishable from empty":
    // each one keeps its rejection reason so the panel can say it failed.
    const bestEffort = (promise) =>
      promise.then(
        (data) => ({ data, error: null }),
        (err) => ({ data: null, error: err?.message || 'Request failed' }),
      )

    Promise.all([
      getAnonymousTrafficOverview(periodDays),
      bestEffort(getAnonymousTrafficTopCells(periodDays)),
      bestEffort(getAnonymousTrafficTimeseries(periodDays)),
      bestEffort(getAnonymousTrafficSuspicion(periodDays)),
      bestEffort(getAnonymousTrafficGeo(periodDays)),
    ])
      .then(([overviewResult, cellsResult, seriesResult, suspicionResult, geoResult]) => {
        if (cancelled) return
        setOverview(overviewResult)
        setTopCells(cellsResult.data)
        setTimeseries(seriesResult.data)
        setSuspicion(suspicionResult.data)
        setGeo(geoResult.data)
        setPanelErrors({
          topCells: cellsResult.error,
          timeseries: seriesResult.error,
          suspicion: suspicionResult.error,
          geo: geoResult.error,
        })
      })
      .catch((err) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [periodDays])

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="spinner"></div>
        <p>Loading anonymous traffic data...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="analytics-error">
        <span>{Icons.alertTriangle}</span>
        <p>{error}</p>
      </div>
    )
  }

  if (!overview) return null

  const requestsPerIp = overview.requests_per_ip || []
  const cellViewsPerIp = overview.cell_views_per_ip || []
  const potentialScrapers = overview.potential_scrapers || []
  const botRequests24h = overview.bot_requests_24h ?? 0
  const topBotUserAgents = overview.top_bot_user_agents || []
  const noRefererIps = overview.no_referer_ips || []
  const cells = topCells?.cells || []
  const buckets = timeseries?.buckets || []
  const scoredIps = suspicion?.scored_ips || []
  const countries = geo?.countries || []

  const periodLabel = `Last ${overview.period_days ?? periodDays} days`
  const totalAnonRequests = requestsPerIp.reduce((sum, row) => sum + (row.requests || 0), 0)
  const criticalIps = scoredIps.filter((row) => (row.suspicion_score || 0) >= 71).length
  // A failed suspicion fetch must not render as a green "0 critical IPs" —
  // that is the false all-clear this whole change exists to prevent.
  const suspicionFailed = Boolean(panelErrors.suspicion)

  return (
    <div className="analytics-anonymous-traffic-view">
      {/* KPI row — panel 14 is the bot stat tile, on its own fixed 24h window */}
      <div className="analytics-kpi-grid">
        <KPICard
          icon={Icons.bot}
          label={`Bot Requests ${WINDOW_24H}`}
          value={formatNumber(botRequests24h)}
          sublabel="Fixed 24h window, ignores the period selector"
          color={botRequests24h > 0 ? 'orange' : 'green'}
        />
        <KPICard
          icon={Icons.globe}
          label="Anonymous Requests"
          value={formatNumber(totalAnonRequests)}
          sublabel={`Top ${requestsPerIp.length} IPs · ${periodLabel}`}
          color="cyan"
        />
        <KPICard
          icon={Icons.alertTriangle}
          label={`Potential Scrapers ${WINDOW_24H}`}
          value={formatNumber(potentialScrapers.length)}
          sublabel="IPs above the hourly-rate threshold"
          color={potentialScrapers.length > 0 ? 'red' : 'green'}
        />
        <KPICard
          icon={Icons.zap}
          label={`Critical IPs ${WINDOW_24H}`}
          value={suspicionFailed ? '—' : formatNumber(criticalIps)}
          sublabel={suspicionFailed
            ? 'Unavailable — the suspicion query failed'
            : 'Suspicion score 71-100'}
          color={suspicionFailed ? 'gray' : (criticalIps > 0 ? 'red' : 'green')}
        />
      </div>

      {/* Panel 15 — bot vs human over time */}
      <div className="analytics-charts-row">
        {panelErrors.timeseries ? (
          <div className="analytics-table-section">
            <div className="anon-panel-header"><h3>Bot vs Human Requests</h3></div>
            <p style={{ padding: '24px', textAlign: 'center', color: COLORS.orange }}>
              <strong>Could not load this panel.</strong>{' '}
              <span style={{ color: COLORS.gray }}>
                Not the same as a quiet period — the data is unknown.
              </span>
              <span style={{ display: 'block', marginTop: '6px', fontSize: '0.85em', color: COLORS.gray }}>
                {panelErrors.timeseries}
              </span>
            </p>
          </div>
        ) : (
          <BotVsHumanChart buckets={buckets} />
        )}
      </div>

      {/* Panel 10 — potential scrapers, fixed 24h window */}
      <Panel
        title="Potential Scrapers"
        caption={`${WINDOW_24H} fixed window · sustained request rate over the scraping threshold`}
      >
        <table className="analytics-table">
          <thead>
            <tr>
              <th>IP (hashed)</th>
              <th className="count-cell">Total Requests</th>
              <th className="count-cell">Requests / Hour</th>
              <th className="count-cell">Unique Cells</th>
            </tr>
          </thead>
          <tbody>
            {potentialScrapers.map((row, idx) => (
              <tr key={row.ip || idx}>
                <IpCell ip={row.ip} />
                <td className="count-cell">{formatNumber(row.total_requests)}</td>
                <td className="count-cell">{formatNumber(row.requests_per_hour)}</td>
                <td className="count-cell">{formatNumber(row.unique_cells)}</td>
              </tr>
            ))}
            {potentialScrapers.length === 0 && (
              <EmptyRow colSpan={4} label="No IP crossed the scraping threshold in the last 24h" />
            )}
          </tbody>
        </table>
      </Panel>

      {/* Panel 18 — IP suspicion ranking, fixed 24h window */}
      <Panel
        title="IP Suspicion Ranking"
        caption={`${WINDOW_24H} fixed window · scored on bot share, missing referer and missing Accept-Language`}
      >
        <div style={{ overflowX: 'auto' }}>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>IP (hashed)</th>
                <th className="count-cell">Score</th>
                <th className="count-cell">Requests</th>
                <th className="count-cell">Unique Cells</th>
                <th className="count-cell">Bot %</th>
                <th className="count-cell">No Referer %</th>
                <th className="count-cell">No Accept-Lang %</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {scoredIps.map((row, idx) => (
                <tr key={row.ip || idx}>
                  <IpCell ip={row.ip} />
                  <td className="count-cell"><ScoreBadge score={row.suspicion_score} /></td>
                  <td className="count-cell">{formatNumber(row.total_requests)}</td>
                  <td className="count-cell">{formatNumber(row.unique_cells)}</td>
                  <td className="count-cell">{formatPct(row.bot_pct)}</td>
                  <td className="count-cell">{formatPct(row.no_referer_pct)}</td>
                  <td className="count-cell">{formatPct(row.no_accept_language_pct)}</td>
                  <td>
                    <div className="anon-flag-list">
                      {(row.flags || []).map((flag) => (
                        <span key={flag} className="anon-flag">{flag.replace(/_/g, ' ')}</span>
                      ))}
                      {(row.flags || []).length === 0 && (
                        <span style={{ color: COLORS.gray }}>-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {scoredIps.length === 0 && (
                panelErrors.suspicion
                  ? <FailedRow colSpan={8} error={panelErrors.suspicion} />
                  : <EmptyRow colSpan={8} />
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Panel 2 — requests per IP */}
      <Panel title="Top IPs by Requests" caption={periodLabel}>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>IP (hashed)</th>
              <th className="count-cell">Requests</th>
            </tr>
          </thead>
          <tbody>
            {requestsPerIp.map((row, idx) => (
              <tr key={row.ip || idx}>
                <IpCell ip={row.ip} />
                <td className="count-cell">{formatNumber(row.requests)}</td>
              </tr>
            ))}
            {requestsPerIp.length === 0 && <EmptyRow colSpan={2} />}
          </tbody>
        </table>
      </Panel>

      {/* Panel 8 — cell views per IP */}
      <Panel title="Cell Views per IP" caption={periodLabel}>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>IP (hashed)</th>
              <th className="count-cell">Views</th>
              <th className="count-cell">Unique Cells</th>
              <th>Last Access</th>
            </tr>
          </thead>
          <tbody>
            {cellViewsPerIp.map((row, idx) => (
              <tr key={row.ip || idx}>
                <IpCell ip={row.ip} />
                <td className="count-cell">{formatNumber(row.views)}</td>
                <td className="count-cell">{formatNumber(row.unique_cells)}</td>
                <td>{formatTimestamp(row.last_access)}</td>
              </tr>
            ))}
            {cellViewsPerIp.length === 0 && <EmptyRow colSpan={4} />}
          </tbody>
        </table>
      </Panel>

      {/* Panel 9 — most-viewed cells by anonymous visitors */}
      <Panel title="Top Cells by Anonymous Views" caption={periodLabel}>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Cell</th>
              <th>BID</th>
              <th className="count-cell">Views</th>
              <th className="count-cell">Unique IPs</th>
            </tr>
          </thead>
          <tbody>
            {cells.map((row, idx) => (
              <tr key={row.product_id || idx}>
                <td title={row.product_id}>{row.name || row.product_id || 'Unknown'}</td>
                <td className="endpoint-cell">{row.bid || '-'}</td>
                <td className="count-cell">{formatNumber(row.views)}</td>
                <td className="count-cell">{formatNumber(row.unique_ips)}</td>
              </tr>
            ))}
            {cells.length === 0 && (
              panelErrors.topCells
                ? <FailedRow colSpan={4} error={panelErrors.topCells} />
                : <EmptyRow colSpan={4} />
            )}
          </tbody>
        </table>
      </Panel>

      {/* Panel 17 — top bot user agents */}
      <Panel title="Top Bot User Agents" caption={periodLabel}>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>User Agent</th>
              <th className="count-cell">Requests</th>
              <th className="count-cell">Unique IPs</th>
            </tr>
          </thead>
          <tbody>
            {topBotUserAgents.map((row, idx) => (
              <tr key={row.user_agent || idx}>
                <td className="endpoint-cell" title={row.user_agent}>
                  {row.user_agent || 'Unknown'}
                </td>
                <td className="count-cell">{formatNumber(row.requests)}</td>
                <td className="count-cell">{formatNumber(row.unique_ips)}</td>
              </tr>
            ))}
            {topBotUserAgents.length === 0 && <EmptyRow colSpan={3} />}
          </tbody>
        </table>
      </Panel>

      {/* Panel 16 — IPs sending no Referer */}
      <Panel
        title="IPs without Referer"
        caption={`${periodLabel} · a missing Referer on cell requests is the classic scripted-client tell`}
      >
        <table className="analytics-table">
          <thead>
            <tr>
              <th>IP (hashed)</th>
              <th className="count-cell">No-Referer Requests</th>
              <th className="count-cell">Bot Requests</th>
              <th className="count-cell">No-Referer Share</th>
            </tr>
          </thead>
          <tbody>
            {noRefererIps.map((row, idx) => (
              <tr key={row.ip || idx}>
                <IpCell ip={row.ip} />
                <td className="count-cell">{formatNumber(row.no_referer_requests)}</td>
                <td className="count-cell">{formatNumber(row.bot_requests)}</td>
                <td className="count-cell">{formatPct(row.no_referer_pct)}</td>
              </tr>
            ))}
            {noRefererIps.length === 0 && <EmptyRow colSpan={4} />}
          </tbody>
        </table>
      </Panel>

      {/* Panel 13 — country table. The geomap is a separate change; this table
          stays regardless, so a centroid gap can never hide a country. */}
      <Panel title="Anonymous Requests by Country" caption={periodLabel}>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Country</th>
              <th>Code</th>
              <th className="count-cell">Requests</th>
            </tr>
          </thead>
          <tbody>
            {countries.map((row, idx) => (
              <tr key={row.country_code || idx}>
                <td>{row.country || row.country_code || 'Unknown'}</td>
                <td className="endpoint-cell">{row.country_code || '-'}</td>
                <td className="count-cell">{formatNumber(row.requests)}</td>
              </tr>
            ))}
            {countries.length === 0 && (
              panelErrors.geo
                ? <FailedRow colSpan={3} error={panelErrors.geo} />
                : <EmptyRow colSpan={3} />
            )}
          </tbody>
        </table>
      </Panel>
    </div>
  )
}
